import { useState } from 'react';
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import MapIcon from '../../assets/map-icon.svg';
import markerIcon from '../../assets/map-marker-icon.svg';
import SpinnerIcon from '../../assets/spinner2.svg';
import { useNavigate } from 'react-router-dom';
import { FaAngleLeft, FaLocationDot } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../contexts/user';
import { useCart } from '../../contexts/cart';
import useToast from '../../hooks/useToast';
import Toast from '../../components/Toast';
import log from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';
import api from '../../api/api';
import { DEFAULT_CENTER } from '../../config/appConfig';

const ServiceArea = () => {
  const { t } = useTranslation();
  const [address, setAddress] = useState(t('dragmap'));
  const { user, idToken, updateProfile, logout, adminImpersonate } = useUser();
  const initialCenter = user && user?.locations?.[0]
    ? [user.locations[0].latitude, user.locations[0].longitude]
    : [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng];
  const [center, setCenter] = useState(initialCenter);
  const [mapKey, setMapKey] = useState(0);
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { toastMessage, toastType, showToast } = useToast();
  const [fullLocation, setfullLocation] = useState(null);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState(null);

  const FixedCenterMarker = () => (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -100%)',
        pointerEvents: 'none',
        zIndex: 999,
      }}
    >
      <img src={markerIcon} alt="center marker" width={30} height={40} />
    </div>
  );

  const MapInteraction = ({ onAddressUpdate }) => {
    const map = useMapEvents({
      dragend: async () => {
        setIsFetchingAddress(true);
        const center = map.getCenter();
        try {
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: center.lat,
              lon: center.lng,
            },
          });
          const data = res.data.response.data;
          setfullLocation(data);
          const addr = data.address;

          const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const city = addr.city || addr.town || addr.village || '';
          const province = addr.state || '';
          const postal = addr.postcode || '';
          const country = addr.country || '';

          const shortProvince = addr.state_code || province.match(/\b[A-Z]{2}\b/)?.[0] || '';
          const formatted = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

          onAddressUpdate(formatted);
        } catch (error) {
          log.error('Reverse geocoding failed:', error);
        } finally {
          setIsFetchingAddress(false);
        }
      },
    });
    return null;
  };

  const showPermissionMessage = (msg) => {
    setPermissionMessage(msg);
    setTimeout(() => setPermissionMessage(null), 3000);
  };

  const goToCurrentLocation = () => {
    if (!navigator.geolocation) {
      showToast(t('geolocationNotSupported'), 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setIsFetchingAddress(true);
        const { latitude, longitude } = position.coords;

        setCenter([latitude, longitude]);
        setMapKey((k) => k + 1);

        // Reverse geocode for address
        try {
          const res = await api.get('/buyer/map/geocode/reverse', {
            params: {
              lat: latitude,
              lon: longitude,
            },
          });
          const data = res.data.response.data;
          setfullLocation(data);
          const addr = data.address;

          const street = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const city = addr.city || addr.town || addr.village || '';
          const province = addr.state || '';
          const postal = addr.postcode || '';
          const country = addr.country || '';

          const shortProvince = addr.state_code || province.match(/\b[A-Z]{2}\b/)?.[0] || '';
          const formatted = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

          setAddress(formatted);
        } catch (error) {
          log.error('Reverse geocoding failed:', error);
          setAddress(t('addressFetchFailed'));
        } finally {
          setIsFetchingAddress(false);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          showPermissionMessage(t('locationPermissionDenied'));
        } else {
          showToast(t('locationRetrievalFailed'), 'error');
        }
        log.error(error);
      },
    );
  };

  const updateUserLocation = async (addr) => {
    const street = [addr.address.housenumber, addr.address.road].filter(Boolean).join(' ');
    const city = addr.address.city || addr.address.county || addr.address.town || addr.address.village || '';
    const province = addr.address.state || '';
    const postal = addr.address.postcode || '';
    const country = addr.address.country || '';
    const shortProvince = province.match(/\b[A-Z]{2}\b/)?.[0] || '';
    const formattedAddress = `${street}, ${city}, ${shortProvince} ${postal}, ${country}`;

    if ((user && idToken) || adminImpersonate) {
      const payload = {
        latitude: addr.lat,
        longitude: addr.lon,
        formattedAddress,
        addressNumber: addr.address.house_number,
        city: addr.address.city,
        zone: addr.address.state,
        isEdit: false,
        adminImpersonateToken: adminImpersonate || null,
        buyerEmail: user?.email,
        buyerId: user?.id,
      };

      try {
        const response = await api.put(
          '/user/locations',
          payload,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          },
        );

        const locations = response.data.response.data.locations;
        if (locations ) {
          updateProfile({ locations: locations });
        }
        navigate('/home');
      } catch (error) {
        log.error('Error saving address:', error);
        const message = error.response?.data?.error || '';
        if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
          navigate('/home');
          sessionStorage.setItem('sessionExpired', 'true');
          sessionStorage.setItem(
            'toast',
            JSON.stringify({
              message: t('sessionExpiredMessage'),
              type: 'error',
            }),
          );
          logout();
          clearCart();
        }
        else {
          showToast(getApiErrorMessage(error, t), 'error');
        }
      }
    }
    else {
      const location = {
        formattedAddress,
        latitude: Number(addr.lat),
        longitude: Number(addr.lon),
      };
      updateProfile({ locations: [location] });
      navigate('/home');
    }
  };
  
  return (
    <div className="relative w-full h-dvh">
      <MapContainer
        key={mapKey}
        center={center}
        zoom={17}
        className="w-full h-full z-0"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <MapInteraction onAddressUpdate={setAddress} />
      </MapContainer>

      {/* Center marker */}
      <FixedCenterMarker />

      {/* Back Button and locate button */}
      <div className="absolute top-5 left-4 flex" >
        <button onClick={() => navigate('/home')} className='w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center mr-3 input-shadow'>
          <FaAngleLeft className='text-base' />
        </button>

        {/* Locate button */}
        <button
          onClick={goToCurrentLocation}
          className='w-12 h-12 bg-black rounded-full flex items-center justify-center mr-2 input-shadow'
          title="Go to my location"
        >
          <img src={MapIcon} alt="MapIcon" />
        </button>
      </div>

      {/* Bottom Centered Text */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center items-center flex-col">
        <div className="bg-white max-w-[800px] w-full rounded-t-2xl boxshadowOne">
          <div className='text-xl mb-5 text-center font-semibold p-2 border-b border-black border-opacity-10'>
            {t('yourlocation')}
          </div>
          <p className="px-3 font-semibold text-base text-[#000000DE] text-center min-h-[48px]">
            {isFetchingAddress ? (
              <div className="flex flex-col gap-1 w-full">
                {/* Shimmer bar 1 */}
                <div className="relative h-3 w-full bg-[#ddd] overflow-hidden rounded">
                  <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                </div>
                {/* Shimmer bar 2 */}
                <div className="relative h-3 w-3/5 mx-auto bg-[#ddd] overflow-hidden rounded">
                  <div className="absolute top-0 left-0 h-full w-1/2 bg-white opacity-30 transform -translate-x-full animate-shimmer"></div>
                </div>
              </div>
            ) : (
              address
            )}
          </p>
          <div className='px-2 pb-2 mt-0'>
            <button
              className='h-11 w-full bg-black text-sm mt-5 text-white flex items-center justify-center rounded font-medium gap-2'
              onClick={() => fullLocation && updateUserLocation(fullLocation)}
              disabled={isFetchingAddress}
            >
              {isFetchingAddress ? (
                <img src={SpinnerIcon} alt="Loading..." className="h-6 w-6 animate-spin" />
              ) : (
                <>
                  <FaLocationDot />
                  {t('setdeliverylocation')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {permissionMessage && (
        <div
          className="fixed left-1/2 transform -translate-x-1/2 bg-[#1B1223CC] text-white px-4 py-4 rounded-lg shadow-lg z-50 text-sm font-medium"
          style={{ bottom: '5.2rem' }}
        >
          {permissionMessage}
        </div>
      )}
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default ServiceArea;
